import Phaser from "phaser";
import { FISH_CENTER_BIAS_STRENGTH, FISH_EDGE_AVOID_MARGIN_PX, FISH_EDGE_AVOID_STRENGTH, FISH_VISUAL_SCALE, FISH_WANDER_STRENGTH } from "../../game/constants";

export interface FishConfig {
    textureKey: string;
    pointValue: number;
    swimSpeed: number;
    species: string;
}

export class FishBase extends Phaser.Physics.Arcade.Sprite {
    readonly pointValue: number;
    readonly species: string;
    protected swimSpeed: number;
    protected changeDirectionAt = 0;

    constructor(scene: Phaser.Scene, x: number, y: number, config: FishConfig) {
        super(scene, x, y, config.textureKey);
        scene.add.existing(this);
        scene.physics.add.existing(this);
        this.pointValue = Number.isFinite(config.pointValue) ? config.pointValue : 0;
        this.species = config.species;
        this.swimSpeed = config.swimSpeed;
        this.setScale(FISH_VISUAL_SCALE);
        this.setCircle(this.displayWidth * 0.45);
        this.setDepth(5);
        this.pickRandomVelocity();
    }

    update(time: number): void {
        if (time >= this.changeDirectionAt) {
            this.pickRandomVelocity();
        }
        const body = this.body as Phaser.Physics.Arcade.Body | null;
        if (body) {
            this.swimWithBias(body.velocity.x, body.velocity.y);
        }
    }

    protected pickRandomVelocity(): void {
        const direction = new Phaser.Math.Vector2(Phaser.Math.FloatBetween(-1, 1), Phaser.Math.FloatBetween(-1, 1));
        if (direction.lengthSq() < 0.0001) {
            direction.set(1, 0);
        }
        direction.normalize();
        this.swimWithBias(direction.x * this.swimSpeed, direction.y * this.swimSpeed);
        this.changeDirectionAt = this.scene.time.now + Phaser.Math.Between(2000, 4000);
    }

    protected swimWithBias(vx: number, vy: number, speed = this.swimSpeed): void {
        const adjusted = this.applyPositionBias(vx, vy, speed);
        this.setVelocity(adjusted.x, adjusted.y);
        if (adjusted.lengthSq() > 0.0001) {
            this.rotation = Math.atan2(adjusted.y, adjusted.x);
        }
    }

    private applyPositionBias(vx: number, vy: number, speed: number): Phaser.Math.Vector2 {
        const baseDirection = new Phaser.Math.Vector2(vx, vy);
        if (baseDirection.lengthSq() < 0.0001) {
            return new Phaser.Math.Vector2(0, 0);
        }
        baseDirection.normalize();

        const worldBounds = this.scene.physics.world.bounds;
        const centerDirection = new Phaser.Math.Vector2(worldBounds.centerX - this.x, worldBounds.centerY - this.y);
        if (centerDirection.lengthSq() > 0.0001) {
            centerDirection.normalize();
        }

        const margin = FISH_EDGE_AVOID_MARGIN_PX;
        const leftPush = Phaser.Math.Clamp((margin - (this.x - worldBounds.left)) / margin, 0, 1);
        const rightPush = Phaser.Math.Clamp((margin - (worldBounds.right - this.x)) / margin, 0, 1);
        const topPush = Phaser.Math.Clamp((margin - (this.y - worldBounds.top)) / margin, 0, 1);
        const bottomPush = Phaser.Math.Clamp((margin - (worldBounds.bottom - this.y)) / margin, 0, 1);
        const edgeDirection = new Phaser.Math.Vector2(leftPush - rightPush, topPush - bottomPush);
        const wanderAngle = this.scene.time.now * 0.002 + this.x * 0.01 + this.y * 0.013;
        const wanderDirection = new Phaser.Math.Vector2(Math.cos(wanderAngle), Math.sin(wanderAngle));

        const blendedDirection = baseDirection
            .clone()
            .scale(Math.max(0, 1 - FISH_CENTER_BIAS_STRENGTH - FISH_WANDER_STRENGTH))
            .add(centerDirection.scale(FISH_CENTER_BIAS_STRENGTH))
            .add(wanderDirection.scale(FISH_WANDER_STRENGTH))
            .add(edgeDirection.scale(FISH_EDGE_AVOID_STRENGTH));

        if (blendedDirection.lengthSq() < 0.0001) {
            return baseDirection.scale(speed);
        }

        return blendedDirection.normalize().scale(speed);
    }
}
